import { useState, useEffect } from 'react'

import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { Tooltip } from '@progress/kendo-react-tooltip'

import ExcelFileImport, {
    EXCEL_DATA_TYPES,
} from '../../GlobalComponents/ExcelFileImport/ExcelFileImport'
import { AWCButton } from '../../GlobalComponents/Inputs/Inputs'
import {
    NotificationManager,
    fqAlert,
    fqConfirmAllowNo,
} from '../../GlobalComponents/NotificationManager/NotificationManager'

import {
    fetchTechPartnerQuotaImport,
    TechPartnerQuotaImportData,
    fetchCheckForTechPartnerQuotaDuplicates,
} from '../../features/sales/salesAPI'
import { hasAOPPermissions } from '../../features/meta/metaAPI'
import { LOADING_STATES } from '../../features/meta/metaSlice'

import styles from './TechPartnerQuotaImport.module.scss'
import './TechPartnerQuotaImport.scss'

export default function TechPartnerQuotaImport() {
    document.title = 'Tech Partner Quota Import'

    const [importData, setImportData] = useState<Array<TechPartnerQuotaImportData>>([])
    const [overwriteDuplicates, setOverwriteDuplicates] = useState<boolean>(false)
    const [existingDuplicates, setExistingDuplicates] =
        useState<Array<TechPartnerQuotaImportData>>()

    const [importWaitingStatus, setImportWaitingStatus] = useState<LOADING_STATES>(
        LOADING_STATES.INIT
    )
    const [saveWaitingStatus, setSaveWaitingStatus] = useState<LOADING_STATES>(LOADING_STATES.INIT)

    const [initialLoading, setInitialLoading] = useState(true)
    const [allowed, setAllowed] = useState<boolean>(false)

    const [errorCount, setErrorCount] = useState<number>(0)

    const [uniqueKey, setUniqueKey] = useState<number>(0)

    const [showSaveSuccessAlert, setShowSaveSuccessAlert] = useState<boolean>()

    useEffect(() => {
        document.title = 'Technology Partner Dashboard'
        initLoad()
    }, [])

    const initLoad = async () => {
        let hasPerms = await hasAOPPermissions()
        setAllowed(hasPerms)
        setInitialLoading(false)
    }

    const resetPage = (showSuccessAlert?: boolean) => {
        setShowSaveSuccessAlert(showSuccessAlert)

        setImportData([])
        setOverwriteDuplicates(false)
        setExistingDuplicates(undefined)
        setImportWaitingStatus(LOADING_STATES.INIT)
        setSaveWaitingStatus(LOADING_STATES.INIT)
        setInitialLoading(false)
        setErrorCount(0)

        // used as key to reset child component state
        setUniqueKey(Date.now())
    }

    useEffect(() => {
        showSaveSuccessAlert &&
            fqAlert({ type: 'success', message: 'Sales Quota imported successfully!' })
    }, [showSaveSuccessAlert])

    const onFileImported = (importedData: any[]) => {
        // clear the QuotaAssignedTo field for any records imported for entire company
        const cleanedData = importedData.map((r) => {
            if (!r.QuotaAssignedTo) return r

            return {
                ...r,
                QuotaAssignedTo: {
                    Value:
                        parseInt(r.QuotaBy.Value) === QUOTA_BY.Company
                            ? ''
                            : r.QuotaAssignedTo.Value.substring(0, 3),
                },
            }
        })

        setErrorCount(cleanedData.reduce((a, b) => a + b.ErrorCount, 0))
        setImportData(cleanedData)
    }

    const findAndMarkDuplicatesInImportedData = (importedData: any[]): any[] => {
        const isDuplicate = (d: any, d2: any): boolean => {
            return (
                d2.VendorID.Value === d.VendorID.Value &&
                d2.Year.Value === d.Year.Value &&
                d2.QuotaBy.Value === d.QuotaBy.Value &&
                d2.Category?.Value === d.Category?.Value &&
                d2.CategoryRollupDivision?.Value === d.CategoryRollupDivision?.Value &&
                d2.QuotaAssignedTo?.Value === d.QuotaAssignedTo?.Value
            )
        }

        const nonDuplicates = importedData.filter(
            (d) => importedData.filter((d2) => isDuplicate(d, d2)).length === 1
        )

        const duplicates = importedData.filter(
            (d) => importedData.filter((d2) => isDuplicate(d, d2)).length > 1
        )

        return [
            ...nonDuplicates,
            ...duplicates.map((r: any) => {
                // mark Row field with duplicate error message
                return {
                    ...r,
                    Row: { ...r.Row, Error: 'Duplicate Import Value!' },
                    ErrorCount: r.ErrorCount + 1,
                }
            }),
        ]
    }

    const findAndMarkMissingRelatedFieldsInImportedData = (importedData: any[]): any[] => {
        const requiredRelatedFieldsAreMapped = (d: any): boolean => {
            return (
                parseInt(d.QuotaBy.Value) === QUOTA_BY.Company ||
                (d.QuotaAssignedTo !== undefined && d.QuotaAssignedTo.Value !== '')
            )
        }

        const missingFields = importedData.filter((d) => !requiredRelatedFieldsAreMapped(d))

        const nonMissingFields = importedData.filter((d) => requiredRelatedFieldsAreMapped(d))

        return [
            ...nonMissingFields,
            ...missingFields.map((r: any) => {
                // mark Row field with missing field error message
                return {
                    ...r,
                    QuotaBy: { ...r.QuotaBy, Error: 'Quota Assigned To Required!' },
                    ErrorCount: r.ErrorCount + 1,
                }
            }),
        ]
    }

    useEffect(() => {
        if (importData.length > 0) checkForDuplicatesInDatabase()
    }, [importData])

    const checkForDuplicatesInDatabase = async () => {
        // check database if data exists for any imported vendor/year combos
        await fetchCheckForTechPartnerQuotaDuplicates(importData).then((duplicates) => {
            setExistingDuplicates(duplicates)
        })
    }

    useEffect(() => {
        if (existingDuplicates === undefined) return

        if (existingDuplicates.length === 0) setOverwriteDuplicates(true)
        else {
            const askUserHowToHandleDuplicates = async () => {
                const confirm = await fqConfirmAllowNo({
                    title: 'Quota Data Already Exists',
                    message:
                        'Sales Quota data has already been loaded for the below tech partners:\n' +
                        ' \n' +
                        `${existingDuplicates
                            .map((d) => {
                                return `${d.VendorID} - ${d.Year}\n`
                            })
                            .join('')}` +
                        ' \n' +
                        'Do you want to overwrite this data?',
                    showCancel: false,
                })
                confirm ? setOverwriteDuplicates(true) : resetPage()
            }
            askUserHowToHandleDuplicates()
        }
    }, [existingDuplicates])

    const saveImportedData = async () => {
        // save mapped data to database
        await fetchTechPartnerQuotaImport(importData)
    }

    const onSaveClick = async () => {
        setSaveWaitingStatus(LOADING_STATES.LOADING)
        if (importData.length > 0) {
            saveImportedData()
                .then(() => setSaveWaitingStatus(LOADING_STATES.DONE))
                .catch((error) => {
                    console.error(error)
                    setSaveWaitingStatus(LOADING_STATES.FAILED)
                })
        }
    }

    useEffect(() => {
        if (saveWaitingStatus === LOADING_STATES.DONE) {
            resetPage(true)
        } else if (saveWaitingStatus === LOADING_STATES.FAILED)
            fqAlert({
                type: 'error',
                title: '',
                message: 'Error importing sales quota data!',
            })
    }, [saveWaitingStatus])

    const TYPES = ['left', 'center', 'right'] as const
    type textAlignment = (typeof TYPES)[number]

    const GridHeaderCell = (cellText: any, textAlignment?: textAlignment) => {
        return (
            <div
                className="grid-cell"
                style={{
                    textAlign: textAlignment ? textAlignment : 'left',
                }}
            >
                {cellText}
            </div>
        )
    }

    const GridCell = (dataItem: any, cell: any, textAlignment?: textAlignment) => {
        let returnElement = <div>{'\u00A0'}</div> //unicode &nbsp;
        if (cell) {
            const cellValue = cell.Value && cell.Value !== '' ? cell.Value : '\u00A0' //unicode &nbsp;
            returnElement = <div>{cellValue}</div>
            const error = dataItem.Error && dataItem.Error !== '' ? dataItem.Error : cell.Error
            if (error && error !== '')
                returnElement = (
                    <div className={'has-error'}>
                        {cellValue}
                        <div className={'dangerIcon'} title={error} />
                    </div>
                )
        }

        return (
            <td className="grid-cell" style={{ textAlign: textAlignment ? textAlignment : 'left' }}>
                {returnElement}
            </td>
        )
    }

    enum QUOTA_BY {
        'Company' = 1,
        'Division',
        'Salesperson',
    }

    const QuotaItemsSection = () => {
        return (
            <div style={{ margin: '0px 5px 0px 15px' }}>
                {importWaitingStatus === LOADING_STATES.INIT && (
                    <div style={{ height: '100%' }} className={'centered'}>
                        Import Items to Preview
                    </div>
                )}
                {importWaitingStatus === LOADING_STATES.DONE && <QuotaItems />}
            </div>
        )
    }

    const QuotaItems = () => {
        const dangerIcons = (element: HTMLElement) => {
            return element.className === 'dangerIcon'
        }

        return (
            <Tooltip anchorElement="target" filter={dangerIcons} position="right">
                {overwriteDuplicates && (
                    <Grid
                        data={importData.sort(
                            (a, b) => (a.Row as any).Value - (b.Row as any).Value
                        )}
                        style={{ borderRadius: '7px', height: '425px' }}
                        filterable={false}
                        sortable={false}
                        scrollable={'scrollable'}
                    >
                        <GridColumn
                            headerCell={() => GridHeaderCell('Row', 'center')}
                            cell={(props: any) =>
                                GridCell(props.dataItem, props.dataItem.Row, 'center')
                            }
                            width={100}
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Tech Partner', 'center')}
                            cell={(props: any) =>
                                GridCell(props.dataItem, props.dataItem.VendorID, 'center')
                            }
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Year')}
                            cell={(props: any) => GridCell(props.dataItem, props.dataItem.Year)}
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Category Rollup Div/BU')}
                            cell={(props: any) =>
                                GridCell(props.dataItem, props.dataItem.CategoryRollupDivision)
                            }
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Category')}
                            cell={(props: any) => GridCell(props.dataItem, props.dataItem.Category)}
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Value', 'right')}
                            cell={(props: any) =>
                                GridCell(
                                    props.dataItem,
                                    {
                                        Value: parseFloat(
                                            props.dataItem.Quota.Value
                                        ).toLocaleString('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                        }),
                                        Error: props.dataItem.Quota.Error,
                                    },
                                    'right'
                                )
                            }
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Quota By')}
                            cell={(props: any) =>
                                GridCell(props.dataItem, {
                                    Value: QUOTA_BY[props.dataItem.QuotaBy.Value],
                                    Error: props.dataItem.QuotaBy.Error,
                                })
                            }
                        />
                        <GridColumn
                            headerCell={() => GridHeaderCell('Assigned To')}
                            cell={(props: any) =>
                                GridCell(props.dataItem, props.dataItem.QuotaAssignedTo)
                            }
                        />
                    </Grid>
                )}
            </Tooltip>
        )
    }

    if (initialLoading)
        return (
            <div className={styles.app}>
                <div className={'centered'}>
                    <div className={'k-i-loading'} style={{ fontSize: 200, height: '90vh' }} />
                </div>
            </div>
        )

    if (!allowed)
        return (
            <div
                className={`${styles.alert} ${styles.alertDanger}`}
                role="alert"
                style={{
                    textAlign: 'center',
                    marginTop: 60,
                }}
            >
                <h3>Permission Denied</h3>
                <p>AOP Permissions are required to use Tech Partner Dashboard!</p>
            </div>
        )

    return (
        <div className={`${styles.quotaImport} quotaImport`}>
            <ExcelFileImport
                key={`excelFileImport${uniqueKey}`}
                returnHeaderRowInResults={true}
                onFileImported={(importedData: any[]) => {
                    importedData = findAndMarkDuplicatesInImportedData(importedData)
                    importedData = findAndMarkMissingRelatedFieldsInImportedData(importedData)
                    onFileImported(importedData)
                    setImportWaitingStatus(LOADING_STATES.DONE)
                }}
                columnList={[
                    {
                        fieldName: 'VendorID',
                        fieldLabel: 'Tech Partner',
                        dataType: EXCEL_DATA_TYPES.String,
                        maxDataLength: 2,
                        required: true,
                    },
                    // {
                    //     fieldName: 'FiscalYearStartMonth',
                    //     fieldLabel: 'Fiscal Year Start Month',
                    // },
                    {
                        fieldName: 'Year',
                        fieldLabel: 'Quota Year',
                        dataType: EXCEL_DATA_TYPES.Integer,
                        required: true,
                    },
                    {
                        fieldName: 'Category',
                        fieldLabel: 'Quota Category',
                        dataType: EXCEL_DATA_TYPES.String,
                        maxDataLength: 50,
                    },
                    {
                        fieldName: 'CategoryRollupDivision',
                        fieldLabel: 'Rollup Division / Business Unit',
                        dataType: EXCEL_DATA_TYPES.String,
                        maxDataLength: 50,
                    },
                    {
                        fieldName: 'Quota',
                        fieldLabel: 'Quota Value',
                        dataType: EXCEL_DATA_TYPES.Decimal,
                        required: true,
                    },
                    {
                        fieldName: 'QuotaBy',
                        fieldLabel: 'Quota By',
                        dataType: EXCEL_DATA_TYPES.Integer,
                        required: true,
                    },
                    {
                        fieldName: 'QuotaAssignedTo',
                        fieldLabel: 'Quota Assigned To',
                        dataType: EXCEL_DATA_TYPES.String,
                        maxDataLength: 5,
                    },
                ]}
            />
            {(() => {
                switch (importWaitingStatus) {
                // case LOADING_STATES.INIT:
                //     return (
                //         <div style={{ height: '100%' }} className={'centered'}>
                //             Import Items to Preview
                //         </div>
                //     )
                case LOADING_STATES.LOADING:
                    return <div className={'k-i-loading'} style={{ fontSize: 60 }} />
                case LOADING_STATES.DONE:
                    return <QuotaItemsSection />
                default:
                    return null
                }
            })()}
            {importWaitingStatus === LOADING_STATES.DONE && overwriteDuplicates && (
                <div className={styles.importButtonContainer}>
                    {errorCount > 0 && (
                        <div className={styles.errorCount}>
                            {errorCount} Error{errorCount === 1 ? '' : 's'}
                        </div>
                    )}
                    <AWCButton type={'secondary'} onClick={() => resetPage()}>
                        Clear
                    </AWCButton>
                    <AWCButton
                        type={errorCount > 0 || !overwriteDuplicates ? 'disabled' : 'primary'}
                        onClick={onSaveClick}
                    >
                        Import Quota
                    </AWCButton>
                </div>
            )}
            {/* Keep at highest common level */}
            <NotificationManager key={'notificationManager'} />
        </div>
    )
}
